import { useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  ResourceTable,
  type Field,
} from "@project/admin/components/resource-table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";

const donationFields: Field[] = [
  { name: "receipt_no", label: "Receipt", showInTable: true },
  {
    name: "donation_date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "category",
    label: "Category",
    type: "select",
    showInTable: true,
    options: [
      "general",
      "medical",
      "bhumi_dan",
      "club",
      "adoption",
      "netramani",
      "sadharmik_kit",
      "event",
      "other",
    ].map(v => ({ value: v, label: v })),
  },
  {
    name: "payment_mode",
    label: "Payment Mode",
    type: "select",
    showInTable: true,
    options: ["Cash", "UPI", "Online", "Cheque", "Bank Transfer"].map(v => ({
      value: v,
      label: v,
    })),
  },
  {
    name: "payment_status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["success", "pending", "failed"].map(v => ({
      value: v,
      label: v,
    })),
  },
  { name: "transaction_id", label: "Transaction ID" },
  { name: "cheque_no", label: "Cheque No" },
  { name: "bank_name", label: "Bank" },
  { name: "purpose", label: "Purpose" },
  { name: "remarks", label: "Remarks", type: "textarea" },
];

const donorFields: Field[] = [
  { name: "full_name", label: "Name", required: true, showInTable: true },
  { name: "mobile", label: "Mobile", type: "tel", showInTable: true },
  { name: "email", label: "Email", type: "email", showInTable: true },
  { name: "city", label: "City", showInTable: true },
  {
    name: "donor_tier",
    label: "Tier",
    type: "select",
    showInTable: true,
    options: ["normal", "silver", "gold", "platinum", "patron"].map(v => ({
      value: v,
      label: v,
    })),
  },
  { name: "pan", label: "PAN" },
  { name: "address", label: "Address", type: "textarea" },
];

const residentFields: Field[] = [
  { name: "full_name", label: "Name", required: true, showInTable: true },
  {
    name: "gender",
    label: "Gender",
    type: "select",
    showInTable: true,
    options: [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
    ],
  },
  { name: "date_of_birth", label: "DOB", type: "date" },
  { name: "mobile", label: "Mobile", type: "tel", showInTable: true },
  { name: "city", label: "City", showInTable: true },
  { name: "room_no", label: "Room", showInTable: true },
  { name: "admission_date", label: "Admission", type: "date" },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["active", "discharged", "deceased", "on_leave"].map(v => ({
      value: v,
      label: v,
    })),
  },
];

const eventFields: Field[] = [
  { name: "title", label: "Title", required: true, showInTable: true },
  {
    name: "event_type",
    label: "Type",
    type: "select",
    showInTable: true,
    options: [
      "donation",
      "religious",
      "medical_camp",
      "festival",
      "meeting",
      "other",
    ].map(v => ({ value: v, label: v })),
  },
  { name: "event_date", label: "Date", type: "date", showInTable: true },
  { name: "goal_amount", label: "Goal (₹)", type: "number", showInTable: true },
  {
    name: "raised_amount",
    label: "Raised (₹)",
    type: "number",
    showInTable: true,
  },
  { name: "venue", label: "Venue", showInTable: true },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["published", "draft", "cancelled"].map(v => ({
      value: v,
      label: v,
    })),
  },
];

const visitorFields: Field[] = [
  { name: "name", label: "Name", required: true, showInTable: true },
  { name: "mobile_number", label: "Mobile", type: "tel", showInTable: true },
  {
    name: "date",
    label: "Visit Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  { name: "reason_for_visit", label: "Reason", showInTable: true },
  { name: "occupation", label: "Occupation", showInTable: true },
  { name: "email", label: "Email", type: "email" },
];

const tiffinFields: Field[] = [
  { name: "full_name", label: "Name", required: true, showInTable: true },
  {
    name: "mobile",
    label: "Mobile",
    type: "tel",
    required: true,
    showInTable: true,
  },
  {
    name: "meal_type",
    label: "Meal",
    type: "select",
    showInTable: true,
    options: ["lunch", "dinner", "both"].map(v => ({ value: v, label: v })),
  },
  { name: "tiffin_count", label: "Count", type: "number", showInTable: true },
  { name: "start_date", label: "Start", type: "date", showInTable: true },
  { name: "city", label: "City", showInTable: true },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["new", "approved", "active", "cancelled", "completed"].map(v => ({
      value: v,
      label: v,
    })),
  },
];

const feedbackFields: Field[] = [
  { name: "full_name", label: "Name", required: true, showInTable: true },
  { name: "mobile", label: "Mobile", type: "tel", showInTable: true },
  {
    name: "feedback_type",
    label: "Type",
    type: "select",
    showInTable: true,
    options: ["suggestion", "complaint", "appreciation", "query", "other"].map(
      v => ({ value: v, label: v })
    ),
  },
  { name: "rating", label: "Rating", type: "number", showInTable: true },
  { name: "message", label: "Message", type: "textarea", showInTable: true },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["new", "reviewed", "resolved", "closed"].map(v => ({
      value: v,
      label: v,
    })),
  },
];

const inquiryFields: Field[] = [
  { name: "full_name", label: "Name", required: true, showInTable: true },
  { name: "mobile", label: "Mobile", type: "tel", showInTable: true },
  { name: "email", label: "Email", type: "email", showInTable: true },
  { name: "subject", label: "Subject", showInTable: true },
  { name: "message", label: "Message", type: "textarea", showInTable: true },
  {
    name: "status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["new", "contacted", "resolved", "closed"].map(v => ({
      value: v,
      label: v,
    })),
  },
];

const medicalFields: Field[] = [
  {
    name: "expense_date",
    label: "Date",
    type: "date",
    required: true,
    showInTable: true,
  },
  { name: "hospital_name", label: "Hospital", showInTable: true },
  { name: "doctor_name", label: "Doctor", showInTable: true },
  {
    name: "amount",
    label: "Amount (₹)",
    type: "number",
    required: true,
    showInTable: true,
  },
  {
    name: "payment_status",
    label: "Status",
    type: "select",
    showInTable: true,
    options: ["pending", "paid", "partial"].map(v => ({ value: v, label: v })),
  },
  { name: "description", label: "Description", type: "textarea" },
];

const punyaTithiFields: Field[] = [
  {
    name: "deceased_name",
    label: "Deceased",
    required: true,
    showInTable: true,
  },
  { name: "family_name", label: "Family", showInTable: true },
  { name: "relation", label: "Relation", showInTable: true },
  { name: "death_date", label: "Death Date", type: "date", showInTable: true },
  { name: "tithi_date", label: "Tithi", showInTable: true },
  { name: "family_mobile", label: "Mobile", type: "tel" },
  { name: "family_email", label: "Email", type: "email" },
];

const TABS: { value: string; label: string; el: React.ReactNode }[] = [
  {
    value: "donations",
    label: "Donations",
    el: (
      <ResourceTable
        table="donations"
        title="Donation"
        fields={donationFields}
        searchFields={["receipt_no", "purpose", "transaction_id", "bank_name"]}
        orderBy="donation_date"
        dateField="donation_date"
        totalField="amount"
        statusField="payment_status"
        statusFilterOptions={["success", "pending", "failed"]}
      />
    ),
  },
  {
    value: "donors",
    label: "Donors",
    el: (
      <ResourceTable
        table="donors"
        title="Donor"
        fields={donorFields}
        searchFields={["full_name", "mobile", "email", "city", "pan"]}
        orderBy="created_at"
      />
    ),
  },
  {
    value: "residents",
    label: "Residents",
    el: (
      <ResourceTable
        table="residents"
        title="Resident"
        fields={residentFields}
        searchFields={["full_name", "mobile", "city", "room_no"]}
        orderBy="full_name"
        orderAsc
        statusField="status"
        statusFilterOptions={["active", "discharged", "deceased", "on_leave"]}
      />
    ),
  },
  {
    value: "events",
    label: "Donation Events",
    el: (
      <ResourceTable
        table="events"
        title="Event"
        fields={eventFields}
        searchFields={["title", "venue", "event_type"]}
        orderBy="event_date"
        dateField="event_date"
        totalField="raised_amount"
        statusField="status"
        statusFilterOptions={["published", "draft", "cancelled"]}
      />
    ),
  },
  {
    value: "visitors",
    label: "Visitors",
    el: (
      <ResourceTable
        table="visitors"
        title="Visitor"
        fields={visitorFields}
        searchFields={[
          "name",
          "mobile_number",
          "reason_for_visit",
          "occupation",
        ]}
        orderBy="date"
        dateField="date"
      />
    ),
  },
  {
    value: "tiffin",
    label: "Tiffin",
    el: (
      <ResourceTable
        table="tiffin_requests"
        title="Tiffin Request"
        fields={tiffinFields}
        searchFields={["full_name", "mobile", "city"]}
        orderBy="created_at"
        statusField="status"
        statusFilterOptions={[
          "new",
          "approved",
          "active",
          "cancelled",
          "completed",
        ]}
      />
    ),
  },
  {
    value: "feedback",
    label: "Feedback",
    el: (
      <ResourceTable
        table="feedback"
        title="Feedback"
        fields={feedbackFields}
        searchFields={["full_name", "mobile", "message"]}
        orderBy="created_at"
        statusField="status"
        statusFilterOptions={["new", "reviewed", "resolved", "closed"]}
      />
    ),
  },
  {
    value: "inquiries",
    label: "Inquiries",
    el: (
      <ResourceTable
        table="contact_inquiries"
        title="Inquiry"
        fields={inquiryFields}
        searchFields={["full_name", "mobile", "email", "subject"]}
        orderBy="created_at"
        statusField="status"
        statusFilterOptions={["new", "contacted", "resolved", "closed"]}
      />
    ),
  },
  {
    value: "medical",
    label: "Medical",
    el: (
      <ResourceTable
        table="resident_medical"
        title="Medical Expense"
        fields={medicalFields}
        searchFields={["hospital_name", "doctor_name", "description"]}
        orderBy="expense_date"
        dateField="expense_date"
        totalField="amount"
        statusField="payment_status"
        statusFilterOptions={["pending", "paid", "partial"]}
      />
    ),
  },
  {
    value: "punya",
    label: "Punya Tithi",
    el: (
      <ResourceTable
        table="punya_tithi"
        title="Punya Tithi"
        fields={punyaTithiFields}
        searchFields={["deceased_name", "family_name", "family_mobile"]}
        orderBy="death_date"
        dateField="death_date"
      />
    ),
  },
];

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState(TABS[0].value);
  const activeReport = TABS.find(t => t.value === activeTab) ?? TABS[0];

  return (
    <AdminShell
      title="Reports"
      subtitle="All trust records — search, filter, edit, delete & export"
    >
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        variant="line"
        className="space-y-4"
      >
        <div className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0">
          <TabsList>
            {TABS.map(t => (
              <TabsTrigger
                key={t.value}
                value={t.value}
                className="shrink-0"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value={activeReport.value} className="mt-4">
          {activeReport.el}
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
}
